/**
 * Reactions on a message.
 *
 * Two pieces, because the sketches put them in two places: the pills sit under
 * the message, while the control that adds one lives in the action row that
 * appears over a message you are pointing at. Both go through the same toggle,
 * so a reaction added from either place is the same request and the same
 * optimistic update.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { ReactElement } from 'react'
import { createPortal } from 'react-dom'

import { api } from '@/api/client'
import { refreshCachedMessage } from '@/api/store'
import type { Message, MessageReaction } from '@/api/types'
import { Avatar, useToast } from '@/components/ui'
import { useExternalImages, userAvatarSrc } from '@/lib/externalImages'
import styles from './MessageReactions.module.css'

const QUICK_REACTIONS = ['👍', '👎', '❤️', '✅', '😂', '🎉', '👀', '🧵', '🚀', '🙏'] as const

function cx(...values: Array<string | false | null | undefined>): string {
  return values.filter((value): value is string => typeof value === 'string' && value !== '').join(' ')
}

interface Toggle {
  /** The emoji currently in flight, or null. */
  saving: string | null
  toggle: (emoji: string) => Promise<void>
}

function useReactionToggle(message: Message, disabled: boolean): Toggle {
  const toast = useToast()
  const [saving, setSaving] = useState<string | null>(null)
  const reactions = useMemo(() => message.reactions ?? [], [message.reactions])

  const toggle = useCallback(
    async (emoji: string): Promise<void> => {
      if (disabled || saving !== null) return
      const mine = reactions.find((reaction) => reaction.emoji === emoji)?.me === true
      setSaving(emoji)
      try {
        const path = `/messages/${encodeURIComponent(message.id)}/reactions`
        const updated = mine
          ? await api.del<Message>(`${path}/${encodeURIComponent(emoji)}`)
          : await api.post<Message>(path, { emoji })
        refreshCachedMessage(updated)
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'The reaction could not be saved.')
      } finally {
        setSaving(null)
      }
    },
    [disabled, message.id, reactions, saving, toast],
  )

  return { saving, toggle }
}

/** Closes `onOutside` on a pointer press anywhere else, while `open`. */
function useDismiss(
  open: boolean,
  rootRef: React.RefObject<HTMLElement | null>,
  floatingRef: React.RefObject<HTMLElement | null>,
  onOutside: () => void,
): void {
  useEffect(() => {
    if (!open) return
    const close = (event: PointerEvent): void => {
      if (
        event.target instanceof Node &&
        !rootRef.current?.contains(event.target) &&
        !floatingRef.current?.contains(event.target)
      ) {
        onOutside()
      }
    }
    document.addEventListener('pointerdown', close)
    return () => document.removeEventListener('pointerdown', close)
  }, [floatingRef, onOutside, open, rootRef])
}

export interface MessageReactionsProps {
  message: Message
  disabled?: boolean
  /**
   * Whether the pills carry their own "add" control. False where the action
   * row over the message already offers one, which is everywhere the message
   * can be pointed at.
   */
  showAdd?: boolean
}

interface ReactionChipProps {
  reaction: MessageReaction
  disabled: boolean
  onToggle: (emoji: string) => void
}

function ReactionChip({ reaction, disabled, onToggle }: ReactionChipProps): ReactElement {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const [hovered, setHovered] = useState(false)
  const [focused, setFocused] = useState(false)
  const [tooltipPosition, setTooltipPosition] = useState<{ left: number; top: number } | null>(null)
  const allowExternalImages = useExternalImages()
  const actors = (reaction.actors ?? []).filter((actor) => actor.name.trim() !== '')
  const names = actors.map((actor) => actor.name.trim()).join(', ')
  const countLabel = `${reaction.count} ${reaction.count === 1 ? 'reaction' : 'reactions'}`
  const showTooltip = actors.length > 0 && (hovered || focused)

  const updateTooltipPosition = useCallback((): void => {
    const button = buttonRef.current
    const tooltip = tooltipRef.current
    if (button === null || tooltip === null) return

    const buttonRect = button.getBoundingClientRect()
    const viewportPadding = 8
    const gap = 8
    const centeredLeft = buttonRect.left + buttonRect.width / 2 - tooltip.offsetWidth / 2
    const maximumLeft = Math.max(viewportPadding, window.innerWidth - tooltip.offsetWidth - viewportPadding)
    const left = Math.min(Math.max(viewportPadding, centeredLeft), maximumLeft)
    const preferredTop = buttonRect.top - tooltip.offsetHeight - gap
    const maximumTop = Math.max(viewportPadding, window.innerHeight - tooltip.offsetHeight - viewportPadding)
    const top =
      preferredTop >= viewportPadding
        ? preferredTop
        : Math.min(buttonRect.bottom + gap, maximumTop)

    setTooltipPosition({ left: Math.round(left), top: Math.round(top) })
  }, [])

  useLayoutEffect(() => {
    if (!showTooltip) {
      setTooltipPosition(null)
      return
    }

    updateTooltipPosition()
    window.addEventListener('resize', updateTooltipPosition)
    window.addEventListener('scroll', updateTooltipPosition, true)
    return () => {
      window.removeEventListener('resize', updateTooltipPosition)
      window.removeEventListener('scroll', updateTooltipPosition, true)
    }
  }, [showTooltip, updateTooltipPosition])

  return (
    <>
      <button
        ref={buttonRef}
        className={cx(styles.chip, reaction.me && styles.mine)}
        type="button"
        aria-pressed={reaction.me}
        aria-label={`${reaction.emoji}, ${countLabel}${names === '' ? '' : `: ${names}`}`}
        disabled={disabled}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onClick={() => onToggle(reaction.emoji)}
      >
        <span aria-hidden="true">{reaction.emoji}</span>
        <span>{reaction.count}</span>
      </button>

      {showTooltip
        ? createPortal(
            <div
              ref={tooltipRef}
              className={cx(styles.tooltip, tooltipPosition !== null && styles.tooltipVisible)}
              role="tooltip"
              style={{ left: tooltipPosition?.left ?? 0, top: tooltipPosition?.top ?? 0 }}
            >
              {actors.map((actor) => (
                <span className={styles.tooltipPerson} key={actor.id}>
                  {/* A reaction actor carries no `is_bot`: bots have no way to
                      react today, so this draws every actor as a person. It
                      still goes through the shared rule so that stays true
                      the day an actor can be a bot. */}
                  <Avatar
                    name={actor.name}
                    id={actor.id}
                    src={userAvatarSrc(actor, allowExternalImages)}
                    size="xs"
                    decorative
                  />
                  <span>{actor.name}</span>
                </span>
              ))}
            </div>,
            document.body,
          )
        : null}
    </>
  )
}

export function MessageReactions({
  message,
  disabled = false,
  showAdd = true,
}: MessageReactionsProps): ReactElement | null {
  const reactions = message.reactions ?? []
  const { saving, toggle } = useReactionToggle(message, disabled)

  if (reactions.length === 0 && (!showAdd || disabled)) return null

  return (
    <div className={styles.reactions}>
      {reactions.map((reaction) => (
        <ReactionChip
          reaction={reaction}
          disabled={disabled || saving !== null}
          key={reaction.emoji}
          onToggle={(emoji) => void toggle(emoji)}
        />
      ))}

      {showAdd && !disabled ? <AddReactionButton message={message} /> : null}
    </div>
  )
}

export interface AddReactionButtonProps {
  message: Message
  disabled?: boolean
  className?: string
}

/** The control that opens the quick picker, wherever it is placed. */
export function AddReactionButton({
  message,
  disabled = false,
  className,
}: AddReactionButtonProps): ReactElement | null {
  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const pickerRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [pickerPosition, setPickerPosition] = useState<{ left: number; top: number } | null>(null)
  const { saving, toggle } = useReactionToggle(message, disabled)

  const close = useCallback(() => setOpen(false), [])
  useDismiss(open, rootRef, pickerRef, close)

  const updatePickerPosition = useCallback((): void => {
    const button = buttonRef.current
    const picker = pickerRef.current
    if (button === null || picker === null) return

    const buttonRect = button.getBoundingClientRect()
    const viewportPadding = 8
    const gap = 6
    const maximumLeft = Math.max(viewportPadding, window.innerWidth - picker.offsetWidth - viewportPadding)
    const left = Math.min(Math.max(viewportPadding, buttonRect.left), maximumLeft)
    const preferredTop = buttonRect.top - picker.offsetHeight - gap
    const maximumTop = Math.max(viewportPadding, window.innerHeight - picker.offsetHeight - viewportPadding)
    const top =
      preferredTop >= viewportPadding
        ? preferredTop
        : Math.min(buttonRect.bottom + gap, maximumTop)

    setPickerPosition({ left: Math.round(left), top: Math.round(top) })
  }, [])

  useLayoutEffect(() => {
    if (!open) {
      setPickerPosition(null)
      return
    }

    updatePickerPosition()
    window.addEventListener('resize', updatePickerPosition)
    window.addEventListener('scroll', updatePickerPosition, true)
    return () => {
      window.removeEventListener('resize', updatePickerPosition)
      window.removeEventListener('scroll', updatePickerPosition, true)
    }
  }, [open, updatePickerPosition])

  if (disabled) return null

  return (
    <div className={styles.pickerRoot} ref={rootRef}>
      <button
        ref={buttonRef}
        className={cx(styles.add, className)}
        type="button"
        aria-label="Add reaction"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((current) => !current)}
        onKeyDown={(event) => {
          if (event.key !== 'Escape' || !open) return
          event.stopPropagation()
          setOpen(false)
        }}
      >
        <svg className={styles.reactionIcon} viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="10.5" cy="12.5" r="7.5" />
          <path d="M8 11h.01M13 11h.01M7.8 14.5c.8 1 1.7 1.5 2.7 1.5s1.9-.5 2.7-1.5M19 3.5v6M16 6.5h6" />
        </svg>
      </button>
      {open
        ? createPortal(
            <div
              ref={pickerRef}
              className={styles.picker}
              role="menu"
              aria-label="Choose a reaction"
              style={{
                left: pickerPosition?.left ?? 0,
                top: pickerPosition?.top ?? 0,
                visibility: pickerPosition === null ? 'hidden' : 'visible',
              }}
              onKeyDown={(event) => {
                if (event.key !== 'Escape') return
                event.stopPropagation()
                close()
                buttonRef.current?.focus()
              }}
            >
              {QUICK_REACTIONS.map((emoji) => (
                <button
                  type="button"
                  role="menuitem"
                  key={emoji}
                  disabled={saving !== null}
                  onClick={() => {
                    setOpen(false)
                    void toggle(emoji)
                  }}
                >
                  {emoji}
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}
    </div>
  )
}
